import { ApplicationRef, ComponentFactoryResolver, EmbeddedViewRef, Injectable, Injector } from '@angular/core';
import Noty from 'noty';

import { LottieComponent } from '@app/shared/lottie/lottie.component';

@Injectable({ providedIn: 'root' })
export class NotyService {
  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private applicationRef: ApplicationRef,
    private injector: Injector
  ) {
    Noty.overrideDefaults({
      theme: 'bootstrap-v4',
      layout: 'topRight',
      progressBar: true,
      timeout: 5000
    } as Noty.Options);
  }

  success(textOrOptions: string | Noty.Options, timeout?: false | number) {
    return this.notyFactory('success', textOrOptions, timeout, 'face-happy');
  }

  error(textOrOptions: string | Noty.Options, timeout?: false | number, requestId?: string | null) {
    return this.notyFactory('error', textOrOptions, timeout, 'face-sad', requestId);
  }

  alert(textOrOptions: string | Noty.Options, timeout?: false | number) {
    return this.notyFactory('alert', textOrOptions, timeout, 'face-neutral');
  }

  warning(textOrOptions: string | Noty.Options, timeout?: false | number) {
    return this.notyFactory('warning', textOrOptions, timeout, 'face-neutral');
  }

  info(textOrOptions: string | Noty.Options, timeout?: false | number) {
    return this.notyFactory('info', textOrOptions, timeout, 'face-neutral');
  }

  private notyFactory(type: Noty.Type, textOrOptions: string | Noty.Options, timeout?: false | number, lottieAnimation?: string, requestId?: string | null) {
    const noty = new Noty({
      ...(typeof textOrOptions === 'string' ? { text: textOrOptions } : textOrOptions),
      ...(timeout === undefined ? {} : { timeout }),
      type
    });

    if (lottieAnimation) {
      this.addLottieAnimation(noty, lottieAnimation);
    }

    if (requestId && requestId !== 'unknown') {
      this.addRequestIdDisplay(noty, requestId);
    }

    noty.show();
  }

  private addLottieAnimation(noty: Noty, lottieAnimation: string) {
    const _that = this;
    let lottieComponentRef: any;

    // Tem que ser function() pra poder usar o 'this'
    // tslint:disable-next-line:only-arrow-functions
    noty.on('onTemplate', function () {
      const scope: { barDom: HTMLElement } = this;

      const contentWrapperHTML = document.createElement('div');
      contentWrapperHTML.classList.add('noty_text');
      contentWrapperHTML.innerHTML = scope.barDom.children[0].innerHTML;
      scope.barDom.children[0].innerHTML = '';

      const componentFactory = _that.componentFactoryResolver.resolveComponentFactory(LottieComponent);
      const componentRef = componentFactory.create(_that.injector);
      _that.applicationRef.attachView(componentRef.hostView);
      lottieComponentRef = componentRef;

      scope.barDom.children[0].appendChild((componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0]);
      scope.barDom.children[0].appendChild(contentWrapperHTML);

      setTimeout(() => {
        lottieComponentRef.instance.name = lottieAnimation;
        lottieComponentRef.instance.width = 70;
        lottieComponentRef.instance.height = 70;
      });
    });

    noty.on('afterClose', () => {
      if (lottieComponentRef) {
        this.applicationRef.detachView(lottieComponentRef.hostView);
        lottieComponentRef.destroy();
      }
    });
  }

  private addRequestIdDisplay(noty: Noty, requestId: string) {
    noty.on('onTemplate', function () {
      const scope: { barDom: HTMLElement } = this;
      const textElement = scope.barDom.querySelector('.noty_text') as HTMLElement;

      if (!textElement) {
        return;
      }

      const truncatedId = requestId.substring(0, 20);

      const requestIdDiv = document.createElement('div');
      requestIdDiv.classList.add('noty-request-id');

      const idContainer = document.createElement('div');
      idContainer.classList.add('noty-request-id__container');
      idContainer.innerHTML = `
        <small class="noty-request-id__text">#${truncatedId}</small>
        <i class="fal fa-copy noty-request-id__icon"></i>
      `;

      requestIdDiv.appendChild(idContainer);
      textElement.appendChild(requestIdDiv);

      const copyFullId = (ev?: Event) => {
        ev?.preventDefault();
        ev?.stopPropagation();
        navigator.clipboard.writeText(requestId);
      };

      scope.barDom.classList.add('noty-copyable');
      scope.barDom.onclick = e => copyFullId(e);
    });
  }
}
